import React, { useState, useEffect } from 'react';
import './AILoadingAnimation.css';

interface AILoadingAnimationProps {
  visible: boolean;
  mode?: 'single' | 'hepan'; // single: 单人报告, hepan: 关系分析报告
  title?: string;
  subtitle?: string;
}

const AILoadingAnimation: React.FC<AILoadingAnimationProps> = ({
  visible,
  mode = 'single',
  title,
  subtitle,
}) => {
  const isHepan = mode === 'hepan';
  const [progress, setProgress] = useState(0);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  // 单人报告金句（行为模型 × 荣格心理学融合版）
  const singleQuotes = [
    "行为模型让你看清'出厂设置'，荣格心理学让你学会与它相处。认账，是为了更好地破局。",
    "荣格：'向外看的人在做梦，向内看的人才清醒。'这份报告帮你向内看。",
    "行为模型告诉你，你是一颗大白菜还是苹果的种子；荣格告诉你，如何长成最脆爽的那颗大白菜。",
    "荣格说：'除非你意识到潜意识，否则它将主导你的人生，而你将其称为发展走向。",
    "看清导航的人，从不抱怨风雨。在雨天读书，在晴天赶路，这就是结构化分析带给现代人最大的体面。",
    "外部世界的一切，都是你内心景象的投影。看清你的出厂说明书，是为了拿回投影仪的开关。",
    "所谓'运气'，不过是你的内在原型在恰当的时机，与外部环境达成了一次共时性的共振。",
    "阴影面（Shadow）不是敌人，而是被压抑的自我。整合它，你才能真正完整。",
    "人格面具（Person）是你给世界看的脸，但真正的自性（Self）藏在更深的地方。",
    "先搞清楚自己开的是什么车，才知道接下来的路该怎么走。",
    "所谓的'命好'，其实是性格与环境达成了高难度的匹配。",
    "当外部世界不可控时，向内求索是现代人最高级的觉醒。",
    "荣格：'除非你使无意识成为意识，否则它将引导你的人生。'意识到，才能超越。",
    "原型不是宿命的枷锁，而是你潜力的地图。认识它，才能突破它。"
  ];

  // 关系分析报告金句（双人关系分析版）
  const hepanQuotes = [
    "两个人的相遇，是两套出厂设置的碰撞。看懂彼此的说明书，才能避免互相拆台。",
    "荣格：'孤独不是因为身边没有人，而是因为无法与他人交流自己最重要的事。'",
    "最好的关系不是互相补足，而是各自完整后的彼此成就。",
    "行为模型看的是你们'是什么'，关系分析看的是你们'在一起会怎样'。",
    "每段关系都是一面镜子，照见你自己都未曾察觉的角落。",
    "两个人的频率对了，1+1>2；频率不对，1+1<1。关系分析就是帮你测频率。",
    "荣格：'被理解的感觉，是人类最深切的渴望。'关系分析帮你理解对方。",
    "不是谁对谁错，而是你们各自带着不同的出厂设置，需要找到兼容模式。",
    "看懂对方的说明书，不是为了改变TA，而是为了知道怎么跟TA相处。",
    "关系中的冲突，往往是两个原型在争夺主导权。",
    "关系分析不是测评，是帮你们看清这段关系的'使用说明'。",
    "荣格：'爱不是彼此凝视，而是一起朝同一个方向看。'",
    "两个人的阶段趋势年度趋势交汇，会产生独特的化学反应。",
    "最好的合伙关系，是你们的做功方式能互相成就，而不是互相消耗。"
  ];

  const quotes = isHepan ? hepanQuotes : singleQuotes;
  const loadingTitle = title || (isHepan ? '正在生成双人关系说明书...' : '正在生成你的人生说明书...');
  const loadingSubtitle = subtitle || (isHepan ? '融合关系结构与西方数据建模' : '融合关系结构与荣格原型理论');

  useEffect(() => {
    if (!visible) {
      setProgress(0);
      setCurrentQuoteIndex(0);
      return;
    }

    // 模拟进度条，慢慢增加到99%（适配 5 分钟请求时间）
    const interval = setInterval(() => {
      setProgress(prev => {
        // 越接近99%，速度越慢
        if (prev >= 99) return 99;
        if (prev >= 95) return prev + 0.03; // 95-99%: 极慢
        if (prev >= 85) return prev + 0.09; // 85-95%: 很慢
        if (prev >= 70) return prev + 0.15; // 70-85%: 较慢
        if (prev >= 50) return prev + 0.24; // 50-70%: 中速
        if (prev >= 30) return prev + 0.36; // 30-50%: 正常
        return prev + 0.48;                 // 0-30%: 较快
      });
    }, 500); // 每500ms更新一次

    // 每4秒切换一条金句
    const quoteInterval = setInterval(() => {
      setCurrentQuoteIndex(prev => (prev + 1) % quotes.length);
    }, 4000);

    return () => {
      clearInterval(interval);
      clearInterval(quoteInterval);
    };
  }, [visible, quotes.length]);

  if (!visible) return null;

  return (
    <div className="ai-loading-overlay">
      <div className="ai-loading-container">
        {/* 背景粒子层 - 行为模型/星座符号漂浮 */}
        <div className="floating-symbols">
          {['甲', '乙', '丙', '丁', '子', '丑', '寅', '卯', '☰', '☷', '☵', '☲', '♈', '♉', '♊', '♋'].map((symbol, i) => (
            <div 
              key={i} 
              className="floating-symbol"
              style={{
                '--delay': `${i * 0.4}s`,
                '--duration': `${8 + i * 0.5}s`,
                '--x': `${10 + (i % 4) * 25}%`,
                '--y': `${10 + Math.floor(i / 4) * 25}%`,
                '--float-y': `${-30 - (i % 3) * 20}px`
              } as React.CSSProperties}
            >
              {symbol}
            </div>
          ))}
        </div>

        {/* 主要动画 - 太极阴阳鱼 */}
        <div className="taiji-container">
          <div className="taiji">
            <div className="taiji-yin"></div>
            <div className="taiji-yang"></div>
            <div className="taiji-dot taiji-dot-yin"></div>
            <div className="taiji-dot taiji-dot-yang"></div>
          </div>
          <div className="taiji-ring ring-1"></div>
          <div className="taiji-ring ring-2"></div>
          <div className="taiji-ring ring-3"></div>
          <div className="taiji-glow"></div>
        </div>

        {/* 流动的文字 */}
        <div className="loading-text-container">
          <div className="loading-text">
            {loadingTitle}
          </div>
          <div className="loading-subtext">
            {loadingSubtitle}
          </div>
        </div>

        {/* 进度条 - 更细腻的渐变 */}
        <div className="progress-bar-container">
          <div className="progress-bar-bg">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${progress}%` }}
            >
              <div className="progress-bar-shine"></div>
            </div>
          </div>
          <div className="progress-text">{Math.floor(progress)}%</div>
        </div>

        {/* 分析金句滚动 - 带淡入淡出 */}
        <div className="quote-container">
          <div className="quote-icon">
            <div className="quote-icon-glow"></div>
            <span>✦</span>
          </div>
          <div className="quote-text">
            {quotes.map((quote, index) => (
              <div
                key={index}
                className={`quote-item ${
                  index === currentQuoteIndex ? 'active' : ''
                } ${
                  index === (currentQuoteIndex - 1 + quotes.length) % quotes.length ? 'prev' : ''
                }`}
              >
                {quote}
              </div>
            ))}
          </div>
        </div>

        {/* 阶段指示器 - 让用户感知进展 */}
        <div className="stage-indicators">
          {[
            { icon: '🔮', label: isHepan ? '解析分析图谱' : '解析行为模型', active: progress >= 0 },
            { icon: '✨', label: isHepan ? '计算契合' : '推演阶段趋势', active: progress >= 35 },
            { icon: '🌟', label: isHepan ? '融合分析' : '映射原型', active: progress >= 65 },
            { icon: '📜', label: '生成报告', active: progress >= 90 }
          ].map((stage, i) => (
            <div 
              key={i} 
              className={`stage-item ${stage.active ? 'active' : ''} ${progress >= (i + 1) * 25 ? 'completed' : ''}`}
            >
              <div className="stage-icon">{stage.icon}</div>
              <div className="stage-label">{stage.label}</div>
              <div className="stage-line"></div>
            </div>
          ))}
        </div>

        {/* 能量波纹效果 */}
        <div className="energy-waves">
          {[...Array(5)].map((_, i) => (
            <div 
              key={i} 
              className="energy-wave"
              style={{
                '--delay': `${i * 0.8}s`,
                '--scale': `${1 + i * 0.3}`
              } as React.CSSProperties}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AILoadingAnimation;
